import React from 'react';
import { Pencil, Save, X, Loader2 } from 'lucide-react';
import { usePageEditMode } from '@/context/PageEditModeContext';

/**
 * Floating "Edit page" toolbar rendered inside any PageEditProvider.
 * Shows only when the current user is an admin.  Provides Enter / Save /
 * Cancel actions and mirrors the existing homepage edit toolbar UX.
 */
export const PageEditToolbar = ({ label = 'Edit page', offset = 0 } = {}) => {
  const { isAdminUser, enabled, enter, save, cancel, saving, dirty } = usePageEditMode();

  if (!isAdminUser) return null;

  return (
    <div
      data-edit-safe
      style={{ bottom: `calc(1rem + ${offset}px)` }}
      className="fixed left-4 z-[9999] bg-black/85 backdrop-blur-md border border-primary/50 rounded-full shadow-2xl px-2 py-1.5 flex items-center gap-2"
      data-testid="page-edit-toolbar"
    >
      {!enabled ? (
        <button
          onClick={enter}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold text-primary hover:bg-primary/10 transition-colors"
          data-testid="page-edit-enter-btn"
        >
          <Pencil className="w-4 h-4" /> {label}
        </button>
      ) : (
        <>
          <span className="text-xs text-yellow-300 px-2">
            {dirty ? '● Unsaved' : 'No changes'}
          </span>
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-primary text-black hover:opacity-90 transition-opacity disabled:opacity-40"
            data-testid="page-edit-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button
            onClick={cancel}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-gray-300 hover:bg-white/10 transition-colors"
            data-testid="page-edit-cancel-btn"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
        </>
      )}
    </div>
  );
};

export default PageEditToolbar;
